import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { LobbyComponent } from "./lobby/lobby.component";
import { BetPanelComponent } from "./bet-panel/bet-panel.component";
import { GameComponent } from "./game/game.component";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { TranslateLoader, TranslateModule, TranslateService } from "@ngx-translate/core";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { AppInitializer } from "./app-initializer";
import { SessionRepository } from "./core/session/session.repository";
import { environment } from "../environments/environment";
import {
    AuthenticationModule, ISessionService,
    LoginUseCase,
    SESSION_REPOSITORY_TOKEN, SESSION_TOKEN
} from "@karma-games/angular-devkit/src/lib/modules/authentication";
import { GetUserUseCase, USER_REPOSITORY_TOKEN, UserModule } from "@karma-games/angular-devkit/src/lib/modules/user";
import { ConfigModule } from "./core/config/config.module";
import { GetConfigUseCase } from "./core/config/get-config.use-case";
import { ButtonsModule } from "@karma-games/angular-devkit/src/lib/modules/button";
import { PopupModule } from "@karma-games/angular-devkit/src/lib/modules/popup";
import { SoundModule } from "@karma-games/angular-devkit/src/lib/modules/sound";
import { DirectivesModule } from "@karma-games/angular-devkit/src/lib/modules/directives";
import { ToastModule } from "@karma-games/angular-devkit/src/lib/modules/toast";
import { SharedComponentsModule } from "@karma-games/angular-devkit/src/lib/modules/shared-components";
import { CURRENCY_TOKEN, PipesModule } from "@karma-games/angular-devkit/src/lib/modules/pipes";
import { LoaderModule } from "@karma-games/angular-devkit/src/lib/modules/loader";
import { ClientSoundService } from "./shared/services/sound/client-sound.service";
import { soundModels } from "./shared/services/sound/sound-models";
import { ErrorPageComponent } from "./error-page/error-page.component";
import { ControlsComponentsModule } from "@karma-games/angular-devkit/src/lib/modules/controls-components";
import { HttpErrorHandlerService } from "./shared/services/http-error-handler.service";
import { AvatarService } from "./shared/services/avatar/avatar.service";
import { AutoplayOptionsComponent } from "./autoplay-options/autoplay-options.component";
import { StatisticsPanelModule } from "./statistics-panel/statistics-panel.module";
import { ClientSharedComponentsModule } from "./shared/components/client-shared-components.module";
import { GetUserLanguageUseCase, SettingsModule, SetInitialSettingsUseCase } from "@karma-games/angular-devkit/src/lib/modules/settings";
import { AvatarChangeModule } from "@karma-games/angular-devkit/src/lib/modules/avatar-change";
import { API_END_POINT, HTTP_ERROR_HANDLER_SERVICE, interceptors } from "@karma-games/angular-devkit/src/lib/services/http";
import { AVATAR_SERVICE } from "@karma-games/angular-devkit/src/lib/services/avatar";
import { COMMUNICATION_CONNECT_OPTIONS, COMMUNICATION_FACTORY_SERVICE, SignalrCommunicationFactory, SOCKET_END_POINT,
    SOCKET_TOKEN, SocketService } from "@karma-games/angular-devkit/src/lib/services/communication";
import { RoundHistoryModule } from "./round-history/round-history.module";
import { LoadPixiAssetsUseCase } from "./game/domain/load-pixi-assets-use-case.service";
import { RoundModule } from "./core/round/round.module";
import { Store, StoreModule } from "@karma-games/angular-devkit/src/lib/extended-store";
import { CurrencyUtils } from "./shared/utils/currency.util";
import {
    NAVIGATION_SERVICE,
    ROOT_CONTAINER_SERVICE,
    RULER_SERVICE,
    TooltipModule
} from "@karma-games/angular-devkit/src/lib/modules/tooltip";
import { UserInfoComponentModule } from "@karma-games/angular-devkit/src/lib/modules/user-info";
import { RootContainerService } from "@karma-games/angular-devkit/src/lib/services/root-container";
import { RulerService } from "@karma-games/angular-devkit/src/lib/services/ruler";
import { NavigationService } from "@karma-games/angular-devkit/src/lib/services/navigation";
import { ChatModule } from "@karma-games/angular-devkit/src/lib/modules/chat";
import { ClientUserRepository } from "./core/user/client-user.repository";
import { BetPanelWalletAnimationDirective } from "./bet-panel/bet-panel-wallet-animation.directive";
import { GameMultiplierDirective } from "./game/game-multiplier.directive";
import { GameNumberColorDirective } from "./game/game-number-color.directive";
import { GAME_NAME_TOKEN } from "@karma-games/angular-devkit/src/lib/interfaces";
import { GameLimitsComponent } from './game-limits/game-limits.component';
import { ClientIosFullscreenComponent } from "./ios-fullscreen/client-ios-fullscreen.component";
import { IOS_FULLSCREEN_COMPONENT, IosFullscreenModule, IosFullscreenHintService } from "@karma-games/angular-devkit/src/lib/modules/ios-fullscreen";
import { GameRulesComponent } from "./game-rules/game-rules.component";
import { WalletAnimationModule } from "@karma-games/angular-devkit/src/lib/modules/wallet-animation";
import { ImagePreloadingService } from "@karma-games/angular-devkit/src/lib/services/image-preloading";
import { TopBarModule } from "@karma-games/angular-devkit/src/lib/modules/top-bar";
import { SideMenuModule } from "@karma-games/angular-devkit/src/lib/modules/side-menu";
import { HistoryModule } from "@karma-games/angular-devkit/src/lib/modules/history";
import { ExceptionTrackingModule, SENTRY_CONFIG, GlobalExceptionHandler } from "@karma-games/angular-devkit/src/lib/modules/exception-tracking";
import { AnalyticsTrackingModule, GA_MEASUREMENT_ID, GA_DEBUG_MODE } from "@karma-games/angular-devkit/src/lib/modules/analytics";
import { FooterModule } from "@karma-games/angular-devkit/src/lib/modules/footer";

export function createTranslateLoader(http: HttpClient, api: string = window.location.origin) {
    return new TranslateHttpLoader(http, `${api}/localizations/`, ".json");
}

@NgModule({
    declarations: [
        AppComponent,
        LobbyComponent,
        BetPanelComponent,
        GameComponent,
        ErrorPageComponent,
        AutoplayOptionsComponent,
        BetPanelWalletAnimationDirective,
        GameMultiplierDirective,
        GameNumberColorDirective,
        GameLimitsComponent,
        ClientIosFullscreenComponent,
        GameRulesComponent
    ],
    imports: [
        StoreModule.forRoot({}),
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        AuthenticationModule,
        UserModule,
        SettingsModule,
        ButtonsModule,
        ConfigModule,
        PopupModule,
        ToastModule,
        SharedComponentsModule,
        PipesModule,
        ControlsComponentsModule,
        ClientSharedComponentsModule,
        SoundModule.forRoot({ models: soundModels, isSoundEnabled: ClientSoundService }),
        DirectivesModule,
        LoaderModule,
        AvatarChangeModule,
        StatisticsPanelModule,
        RoundHistoryModule,
        RoundModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: (createTranslateLoader),
                deps: [HttpClient]
            }
        }),
        ChatModule,
        TooltipModule,
        UserInfoComponentModule,
        IosFullscreenModule,
        WalletAnimationModule.forRoot(),
        TopBarModule,
        SideMenuModule,
        HistoryModule,
        AnalyticsTrackingModule,
        ExceptionTrackingModule,
        FooterModule
    ],
    providers: [
        {
            provide: SOCKET_TOKEN,
            useFactory: (sessionService: ISessionService) => sessionService.getSession()?.token || "",
            deps: [SESSION_TOKEN]
        },
        { provide: COMMUNICATION_FACTORY_SERVICE, useClass: SignalrCommunicationFactory },
        SocketService,
        ClientSoundService,
        TranslateService,
        { provide: GAME_NAME_TOKEN, useValue: "rlgl" },
        { provide: USER_REPOSITORY_TOKEN, useClass: ClientUserRepository },
        { provide: NAVIGATION_SERVICE, useExisting: NavigationService },
        { provide: RULER_SERVICE, useExisting: RulerService },
        { provide: ROOT_CONTAINER_SERVICE, useExisting: RootContainerService },
        { provide: AVATAR_SERVICE, useClass: AvatarService },
        { provide: HTTP_ERROR_HANDLER_SERVICE, useClass: HttpErrorHandlerService },
        interceptors,
        { provide: API_END_POINT, useFactory: () => environment.endPoints.api },
        { provide: SOCKET_END_POINT, useFactory: () => environment.endPoints.socketServer },
        { provide: SESSION_REPOSITORY_TOKEN, useClass: SessionRepository },
        { provide: CURRENCY_TOKEN, useFactory: CurrencyUtils.getCurrencyFromUser, deps: [Store] },
        IosFullscreenHintService,
        { provide: IOS_FULLSCREEN_COMPONENT, useValue: ClientIosFullscreenComponent },
        {
            provide: APP_INITIALIZER,
            useFactory: AppInitializer.initializeApp,
            deps: [LoginUseCase, GetUserUseCase, GetConfigUseCase, GetUserLanguageUseCase, TranslateService, ImagePreloadingService,
                SetInitialSettingsUseCase, LoadPixiAssetsUseCase],
            multi: true
        },
        {
            provide: COMMUNICATION_CONNECT_OPTIONS,
            useFactory: (sessionService: ISessionService) => {
                return {
                    url: environment.endPoints.socketServer,
                    token: sessionService.getSession()?.token || "",
                    autoConnect: false,
                    reconnectionDelay: 2000,
                    reconnectionDelayMax: 2000,
                    reconnectionAttempts: 5,
                    transports: "websocket"
                };
            },
            deps: [SESSION_TOKEN]
        },
        { provide: SENTRY_CONFIG, useValue: environment.sentryConfig },
        { provide: ErrorHandler, useClass: GlobalExceptionHandler },
        { provide: GA_MEASUREMENT_ID, useValue: environment.gaMeasurementId },
        { provide: GA_DEBUG_MODE, useValue: !environment.production }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
